import { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, Search } from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Load Google Maps script
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }

    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google));
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const PlaceAutocomplete = ({ onPlaceSelect, placeholder = "Search for a place...", value = "", onChange }) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  
  const [inputValue, setInputValue] = useState(value);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Click-away listener to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    // Add listener when dropdown is shown
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showDropdown]);

  // Initialize Google Places services
  useEffect(() => {
    let isMounted = true;

    const initServices = async () => {
      try {
        await loadGoogleMapsScript();
        
        if (!isMounted) return;

        // Use AutocompleteService for predictions (renders our own dropdown)
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        
        // Create a dummy div for PlacesService (required for getDetails)
        const dummyDiv = document.createElement('div');
        placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
        
        // Create session token for billing optimization
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();

        setIsLoading(false);
      } catch (err) {
        console.error("Failed to load Google Maps:", err);
        setError("Failed to load location search");
        setIsLoading(false);
      }
    };

    initServices();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch predictions when input changes
  const fetchPredictions = useCallback((query) => {
    if (!query || query.length < 2 || !autocompleteServiceRef.current) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: query,
        types: ["establishment", "geocode"],
        sessionToken: sessionTokenRef.current,
      },
      (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowDropdown(true);
          setHighlightedIndex(-1);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      }
    );
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPredictions(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, fetchPredictions]);

  // Handle place selection
  const handleSelectPlace = (prediction) => {
    if (!placesServiceRef.current) return;

    // Get full place details
    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["name", "formatted_address", "geometry", "place_id", "types"],
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const placeData = {
            name: place.name || place.formatted_address,
            address: place.formatted_address,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            placeId: place.place_id,
            types: place.types || [],
          };

          setInputValue(place.name || place.formatted_address);
          setShowDropdown(false);
          setPredictions([]);
          
          // Create new session token for next search
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
          
          if (onChange) onChange(place.name || place.formatted_address);
          if (onPlaceSelect) onPlaceSelect(placeData);
        }
      }
    );
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) onChange(newValue);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < predictions.length) {
          handleSelectPlace(predictions[highlightedIndex]);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  const handleBlur = (e) => {
    // Only hide if the related target is not within our container
    // This allows clicking on dropdown items
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
      // Small delay to allow click events to register
      setTimeout(() => {
        setShowDropdown(false);
      }, 150);
    }
  };

  if (error) {
    return (
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          className="h-12 bg-[#121212] border-white/10 focus:border-bronze pl-10"
        />
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <p className="text-xs text-red-500 mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <Input
        ref={inputRef}
        data-testid="place-search-input"
        type="text"
        placeholder={isLoading ? "Loading..." : placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => predictions.length > 0 && setShowDropdown(true)}
        onBlur={handleBlur}
        disabled={isLoading}
        className="h-12 bg-[#121212] border-white/10 focus:border-bronze pl-10 pr-10"
        autoComplete="off"
      />
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
      {isLoading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bronze animate-spin" />
      )}

      {/* Custom dropdown - rendered inside the component, works in modals */}
      {showDropdown && predictions.length > 0 && (
        <div 
          className="absolute z-[9999] w-full mt-1 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl overflow-hidden"
          data-testid="place-autocomplete-dropdown"
        >
          {predictions.map((prediction, index) => (
            <div
              key={prediction.place_id}
              data-testid={`place-option-${index}`}
              className={`px-3 py-3 cursor-pointer flex items-start gap-3 transition-colors ${
                index === highlightedIndex 
                  ? "bg-bronze/20 text-white" 
                  : "text-gray-300 hover:bg-white/5"
              }`}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur before click
                handleSelectPlace(prediction);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <MapPin className="w-4 h-4 text-bronze mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">
                  {prediction.structured_formatting?.main_text || prediction.description}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {prediction.structured_formatting?.secondary_text || ""}
                </p>
              </div>
            </div>
          ))}
          <div className="px-3 py-2 bg-[#121212] border-t border-white/5">
            <p className="text-[10px] text-gray-600 flex items-center gap-1">
              <img 
                src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png" 
                alt="Powered by Google" 
                className="h-3 opacity-50 invert"
              />
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaceAutocomplete;
